import { getBasePath } from '@/utils/utils';
import { currentDayPagesConfig } from '@op/constants/page';
import { gptReport } from '@op/services';
import { mdContentToHtml } from '@op/utils/text';
import { getFilterDefaultDay } from '@op/utils/time';
import { objToUrl } from '@op/utils/url';
import { useUpdateEffect } from 'ahooks';
import axios from 'axios';
import moment from 'moment';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'umi';

const defaultStorageValue = getFilterDefaultDay();

export function useGptData({
  gameInfo,
  setTipsVisible,
  reportId,
  reportType = 1,
  defaultParams = {},
}: any) {
  const location = useLocation();
  const [hasDataTime, setHasDataTime] = useState({
    start_time: '',
    end_time: '',
  });

  const [mdFileUrl, setMdFileUrl] = useState('');
  const isProd = true;
  const [empty, setEmpty] = useState(isProd);
  const [renderContentInfo, setRenderContentInfo] = useState({
    html: '',
    headings: [],
    defaultNavigator: '',
  });
  const [navigator, setNavigator] = useState('');
  const [loading, setLoading] = useState(true);

  const defaultValue = (() => {
    const dayPagesConfig =
      currentDayPagesConfig[gameInfo.edition_unified_id] || {};
    const obj = reportId === '3' ? dayPagesConfig : {};
    const defaultValue = Object.assign(
      {},
      defaultStorageValue,
      obj,
      defaultParams,
    );

    return defaultValue;
  })();

  const [languageValue, setLanguageValue] = useState<string>(
    defaultValue.language,
  );
  const [time, setTime] = useState<[string, string]>([
    defaultValue.start_time,
    defaultValue.end_time,
  ]);

  function initData() {
    setRenderContentInfo({
      html: '',
      headings: [],
      defaultNavigator: '',
    });
    setNavigator('');
    setMdFileUrl('');
    setEmpty(true);
  }

  function setUrlInfo(obj: any = {}) {
    const { pathname, query } = location;
    const paramsObj: any = {
      gameid: query.gameid || gameInfo.edition_unified_id,
      ...obj,
    };
    if (query.tab) {
      paramsObj.tab = query.tab;
    }
    const urlParams = objToUrl(paramsObj);
    const url = `${getBasePath() + pathname}?${urlParams}`;
    window.history.replaceState(null, '', url);
  }

  const params = useMemo(() => {
    if (!languageValue) {
      return {};
    }
    const [start_time, end_time] = time;
    return {
      ...gameInfo,
      kind: Number(reportId),
      start_time,
      end_time,
      language: languageValue,
      type: Number(reportType),
    };
  }, [languageValue, time, reportType]);

  useUpdateEffect(() => {
    const [start_time, end_time] = time;
    setUrlInfo({
      start_time,
      end_time,
      language: languageValue,
    });
  }, [languageValue, time]);

  const analysisMdContent = (text: string) => {
    const { html, headings, navigator } = mdContentToHtml(text);
    setRenderContentInfo({
      html,
      headings,
      defaultNavigator: navigator,
    });
    setNavigator(navigator);
    setLoading(false);
  };

  function getMdFile(url: string) {
    if (!url) return;
    setEmpty(false);
    setMdFileUrl(url);
    axios.get(url).then((res) => {
      if (res?.data) {
        const text = res.data;
        analysisMdContent(text);
      }
    });
  }

  const isFirst = useRef(true);
  const requestNumber = useRef(0);
  const getReportData = async (params: any) => {
    setLoading(true);
    initData();

    gptReport(params)
      .then((data: any) => {
        const { list, has_data_end_time, has_data_start_time } = data;
        if (has_data_start_time) {
          setHasDataTime({
            start_time: has_data_start_time,
            end_time: has_data_end_time,
          });
        }

        if (list && Array.isArray(list) && list.length > 0) {
          const [{ md_file: mdFile }] = list;

          getMdFile(mdFile);

          isFirst.current = false;
        } else {
          // 如果第一次进入，那么就再次请求数据。如果3次请求都没有数据，那么就直接无数据
          if (
            isFirst.current &&
            has_data_end_time &&
            requestNumber.current < 3
          ) {
            setTime([has_data_start_time, has_data_end_time]);
            requestNumber.current += 1;
            return;
          }
          // 无数据，然后如果是单天，那么就展示提示弹窗
          const hasToday = moment().isBetween(
            moment(params?.start_time),
            moment(params?.end_time),
            'day',
            '[]',
          );
          if (hasToday) {
            setTipsVisible(true);
          }
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (params.edition_unified_id) {
      getReportData(params);
    }
  }, [params]);

  return {
    time,
    setTime,
    empty,
    mdFileUrl,
    renderContentInfo,
    loading,
    hasDataTime,
    languageValue,
    setLanguageValue,
    setHasDataTime,
    navigator,
    setNavigator,
  };
}

export function useSimpleGptData({
  filter,
  defaultValue,
  setDefaultValue,
}: any) {
  const isFirst = useRef(true);
  const requestNumber = useRef(0);
  const [mdFileUrl, setMdFileUrl] = useState('');
  const [renderContentInfo, setRenderContentInfo] = useState({
    html: '',
    headings: [],
    defaultNavigator: '',
  });
  const [recentReport, setRecentReport] = useState({});

  const [navigator, setNavigator] = useState('');
  const [loading, setLoading] = useState(true);

  function initData() {
    setRenderContentInfo({
      html: '',
      headings: [],
      defaultNavigator: '',
    });
    setNavigator('');
    setMdFileUrl('');
  }

  const analysisMdContent = (text: string) => {
    const { html, headings, navigator } = mdContentToHtml(text);
    setRenderContentInfo({
      html,
      headings,
      defaultNavigator: navigator,
    });
    setNavigator(navigator);
    setLoading(false);
  };

  function getMdFile(url: string) {
    if (!url) {
      setMdFileUrl('');
      setLoading(false);
      return;
    }
    setMdFileUrl(url);
    axios
      .get(url)
      .then((res) => {
        if (res?.data) {
          const text = res.data;
          analysisMdContent(text);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  }

  const getReportData = async (params: any) => {
    setLoading(true);
    initData();

    gptReport(params).then((data: any) => {
      const { list, has_data_end_time, has_data_start_time } = data;

      if (list && Array.isArray(list) && list.length > 0) {
        const [{ md_file: mdFile }] = list;
        getMdFile(mdFile);
        isFirst.current = false;
      } else {
        // 如果第一次进入，那么就再次请求数据。如果3次请求都没有数据，那么就直接无数据
        if (
          isFirst.current &&
          has_data_end_time &&
          requestNumber.current < 3 &&
          !defaultValue?.start_time &&
          !defaultValue?.end_time
        ) {
          setDefaultValue({
            start_time: has_data_start_time,
            end_time: has_data_end_time,
          });
          requestNumber.current += 1;
          return;
        }
        setRecentReport({
          start_time: has_data_start_time,
          end_time: has_data_end_time,
        });
        getMdFile('');
      }
    });
  };

  useEffect(() => {
    if (filter?.edition_unified_id) {
      getReportData(filter);
    }
  }, [filter]);

  return {
    mdFileUrl,
    renderContentInfo,
    loading,
    navigator,
    setNavigator,
    recentReport,
  };
}
